import * as moment from 'moment'
import { PlantHalt } from '../../../../../types/plantHalts'
import { RIC_SG_SHIFTS } from './config'

/**
 * Ore in cui comincia un turno, ordinate: con i turni attuali 6, 14, 22.
 */
const shiftStartHours: number[] = RIC_SG_SHIFTS.map(shift => shift.startHour).sort((a, b) => a - b)

/**
 * Confini di turno strettamente compresi fra `from` e `to`.
 *
 * Si scorrono i giorni coperti dall'intervallo invece di assumerne uno solo: un fermo può
 * durare più di ventiquattro ore, e l'elenco dei fermi può coprire più giorni.
 */
const shiftBoundariesBetween = (from: moment.Moment, to: moment.Moment): moment.Moment[] => {
  const boundaries: moment.Moment[] = []
  const day = from.clone().startOf('day')
  const lastDay = to.clone().startOf('day')

  while (day.isSameOrBefore(lastDay)) {
    shiftStartHours.forEach(hour => {
      const boundary = day.clone().hour(hour)
      if (boundary.isAfter(from) && boundary.isBefore(to)) {
        boundaries.push(boundary)
      }
    })
    day.add(1, 'day')
  }

  return boundaries.sort((a, b) => a.valueOf() - b.valueOf())
}

/**
 * Taglia i fermi sui confini di turno e sulla finestra visualizzata.
 *
 * Un fermo che attraversa un cambio turno diventa due righe distinte, ciascuna con il tempo
 * che compete al proprio turno. Resta però **un solo fermo a database**: i frammenti
 * conservano l'`id` dell'originale, quindi motivarne uno motiva l'intero fermo e l'operatore
 * del turno successivo non deve riscrivere nulla.
 *
 * Il ritaglio sulla finestra serve anche a recuperare i fermi a cavallo della giornata: la
 * pagina li chiede al backend allargando il periodo di un giorno per lato, perché l'API
 * filtra per contenimento (`From >= from && To <= to`) e da sola li scarterebbe.
 */
export const splitHaltsByShift = (
  halts: PlantHalt[],
  windowFrom: moment.Moment,
  windowTo: moment.Moment
): PlantHalt[] => {
  const fragments: PlantHalt[] = []

  halts.forEach(halt => {
    if (!halt.from || !halt.to) {
      return
    }

    const start = moment.max(halt.from.clone(), windowFrom.clone())
    const end = moment.min(halt.to.clone(), windowTo.clone())
    if (!end.isAfter(start)) {
      return
    }

    const edges = [start].concat(shiftBoundariesBetween(start, end)).concat([end])

    for (let i = 0; i < edges.length - 1; i++) {
      const seconds = edges[i + 1].diff(edges[i], 'seconds')
      // Un frammento di durata nulla non si crea: il costruttore di `PlantHalt` azzera a
      // `null` i valori falsy, quindi `duration: 0` diventerebbe una durata vuota.
      if (seconds <= 0) {
        continue
      }
      fragments.push(new PlantHalt({ ...halt, from: edges[i], to: edges[i + 1], duration: seconds }))
    }
  })

  return fragments
}
